import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatNumber, toPercent } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { MovementItem } from './movement.api';

/**
 * The products behind a grouping row, each expanding to its modifiers.
 *
 * Not a `SortableTable`: these rows expand, and the modifier breakdown is a
 * named client requirement rather than a nicety, so it gets a real sub-table
 * instead of being folded into a tooltip. Sorting is by the one column that
 * matters here — what the product sold for — because the list is already
 * narrowed to a single division, group or category by the time it is asked
 * for.
 */

function itemName(item: MovementItem): string {
  return (
    [item.brand, item.name, item.description]
      .filter((part) => part?.trim())
      .join(' ')
      .trim() || (item.upc ?? '—')
  );
}

/** True when the item carries modifier revenue worth opening. */
function hasModifiers(item: MovementItem): boolean {
  return (item.modifiers?.length ?? 0) > 0;
}

export function MovementItems({
  items,
  loading,
  busy,
  emptyMessage,
}: {
  items: MovementItem[];
  loading?: boolean;
  /** Items are on screen and being refreshed: they stay, dimmed, under a spinner. */
  busy?: boolean;
  emptyMessage: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="divide-y rounded-xl border">
        {Array.from({ length: 6 }).map((_, row) => (
          <div key={row} className="flex items-center gap-4 px-4 py-3">
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="relative rounded-xl">
        <BusyOverlay show={busy} />
        <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
          {emptyMessage}
        </p>
      </div>
    );
  }

  const sorted = [...items].sort((a, b) => b.totalSales - a.totalSales);

  return (
    // No scroll box of its own: `Table` scrolls inside its container, and an
    // overflow wrapper here would pin the busy spinner instead of letting it
    // stick to the visible part of a long list.
    <div className="rounded-xl border">
      <Table busy={busy}>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>{t('movement.item')}</TableHead>
            <TableHead>{t('movement.upc')}</TableHead>
            <TableHead className="text-right">{t('movement.units')}</TableHead>
            <TableHead className="text-right">
              {t('movement.priceTotal')}
            </TableHead>
            <TableHead className="text-right">
              {t('movement.stateTax')}
            </TableHead>
            <TableHead className="text-right">
              {t('movement.municipalTax')}
            </TableHead>
            <TableHead className="text-right">
              {t('movement.modifiers')}
            </TableHead>
            <TableHead className="text-right">
              {t('movement.totalSales')}
            </TableHead>
            <TableHead className="text-right">
              {t('movement.avgPrice')}
            </TableHead>
            <TableHead className="text-right">{t('movement.avgCost')}</TableHead>
            <TableHead className="text-right">{t('movement.profit')}</TableHead>
            <TableHead className="text-right">{t('movement.margin')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((item) => {
            const key = item.upc ?? itemName(item);
            const open = expanded === key;
            const expandable = hasModifiers(item);
            // The three modifier figures are reported separately by the API
            // and only ever read together.
            const modifierTotal =
              item.modifierSubtotal +
              item.modifierStateTax +
              item.modifierMunicipalTax;

            return (
              <Fragment key={key}>
                <TableRow>
                  <TableCell className="p-0 pl-2">
                    {expandable && (
                      <button
                        type="button"
                        onClick={() => setExpanded(open ? null : key)}
                        aria-expanded={open}
                        aria-label={t('movement.showModifiers', {
                          item: itemName(item),
                        })}
                        className="hover:bg-muted rounded p-1"
                      >
                        <ChevronDown
                          className={cn(
                            'size-4 transition-transform',
                            open && 'rotate-180'
                          )}
                        />
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="max-w-xs truncate font-medium">
                    {itemName(item)}
                  </TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {item.upc || '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumber(item.amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.priceTotal)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.stateTaxTotal)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.municipalTaxTotal)}
                  </TableCell>
                  <TableCell className="text-right">
                    {modifierTotal ? formatCurrency(modifierTotal) : '—'}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(item.totalSales)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.priceAverage)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.cost)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.profit)}
                  </TableCell>
                  <TableCell className="text-right">
                    {toPercent(item.profitMargin).toFixed(1)}%
                  </TableCell>
                </TableRow>

                {open && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={13} className="bg-muted/30 p-0">
                      <div className="p-3">
                        <p className="text-muted-foreground mb-2 text-xs font-medium">
                          {t('movement.modifiersOn', { item: itemName(item) })}
                        </p>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t('movement.modifier')}</TableHead>
                              <TableHead className="text-right">
                                {t('movement.units')}
                              </TableHead>
                              <TableHead className="text-right">
                                {t('movement.priceTotal')}
                              </TableHead>
                              <TableHead className="text-right">
                                {t('movement.avgPrice')}
                              </TableHead>
                              <TableHead className="text-right">
                                {t('movement.stateTax')}
                              </TableHead>
                              <TableHead className="text-right">
                                {t('movement.municipalTax')}
                              </TableHead>
                              <TableHead className="text-right">
                                {t('movement.totalSales')}
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(item.modifiers ?? []).map((modifier, index) => (
                              <TableRow key={`${modifier.name}-${index}`}>
                                <TableCell>{modifier.name || '—'}</TableCell>
                                <TableCell className="text-right">
                                  {formatNumber(modifier.amount)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(modifier.priceTotal)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(modifier.priceAverage)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(modifier.stateTaxTotal)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(modifier.municipalTaxTotal)}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {formatCurrency(modifier.totalSales)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
